import type { AvailableUnit } from '@/core/api/models/properties';
import { unitsService } from '@/core/api/services/unitsService';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import {
  UNITS_NO_PRICE_LIMIT_AED,
  UNITS_PAGE_SIZE_WEB,
} from '@/modules/properties-web/hooks/useAvailableUnits';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

/** Safety stop if the backend keeps reporting `hasNext`. */
const LOOKUP_MAX_PAGES = 30;

function isLastPage(
  response: Readonly<{ hasNext?: boolean | string | undefined; units: AvailableUnit[] }>,
): boolean {
  return (
    response.units.length === 0 ||
    response.hasNext === false ||
    String(response.hasNext) === 'false'
  );
}

async function findAvailableUnitById(unitId: string): Promise<AvailableUnit | null> {
  const seenIds = new Set<string>();
  for (let page = 0; page < LOOKUP_MAX_PAGES; page += 1) {
    const response = await unitsService.getAvailableUnits({
      page,
      limit: UNITS_PAGE_SIZE_WEB,
      maxPrice: UNITS_NO_PRICE_LIMIT_AED,
    });
    const units = Array.isArray(response.units) ? response.units : [];
    const match = units.find((unit) => unit.unit_id === unitId);
    if (match) {
      return match;
    }
    const hasNewUnits = units.some((unit) => unit?.unit_id && !seenIds.has(unit.unit_id));
    if (!hasNewUnits || isLastPage({ hasNext: response.hasNext, units })) {
      return null;
    }
    units.forEach((unit) => seenIds.add(unit.unit_id));
  }
  logger.warn('useDeepLinkedUnitLookup', 'Stopped paging before finding unit', { unitId });
  return null;
}

/**
 * Details opened by URL (e.g. master plan link right after login) for a unit that
 * is not in the listing replica: page the catalogue without a price limit and
 * seed the store so the details screen and reservation flows can resolve it.
 */
export function useDeepLinkedUnitLookup(
  unitId: string | undefined,
  enabled: boolean,
): Readonly<{ isLookingUp: boolean }> {
  const projectId = useAppStore((s) => s.adhaLoanDetails?.projectId ?? '');
  const upsertAvailableUnitFirst = useAppStore((s) => s.upsertAvailableUnitFirst);

  const query = useQuery({
    queryKey: ['adhaDeepLinkedUnit', projectId, unitId ?? ''],
    queryFn: () => findAvailableUnitById(unitId ?? ''),
    enabled: enabled && Boolean(unitId),
    retry: 1,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const foundUnit = query.data;
  useEffect(() => {
    if (foundUnit) {
      upsertAvailableUnitFirst(foundUnit);
    }
  }, [foundUnit, upsertAvailableUnitFirst]);

  return { isLookingUp: enabled && Boolean(unitId) && query.isPending };
}
